<template>
    <!-- Loading -->
    <div v-if="isLoading && !hasValidData" class="d-flex align-center justify-center" style="min-height: 70vh;">
        <v-row class="d-flex align-center justify-center">
            <v-col class="d-flex flex-column align-center justify-center" cols="12">
                <v-progress-circular size="64" width="3" indeterminate></v-progress-circular>
                <div class="text-center mt-4">
                    <span class="font-roboto text-subtitle-1">
                        {{ $t('message.transactions_admin_plus.loading_financial_data') }}
                    </span>
                </div>
            </v-col>
        </v-row>
    </div>

    <!-- Empty state - no financial data -->
    <div v-else-if="shouldShowEmpty" class="d-flex align-center justify-center" style="min-height: 70vh;">
        <v-row class="d-flex align-center justify-center">
            <v-col class="d-flex flex-column align-center justify-center" cols="12">
                <v-img class="mb-6" height="130" :src="require('@/assets/icons/admin+/financial_overview.svg')"
                    contain></v-img>
                <div class="text-center mb-6">
                    <span class="font-roboto text-empty">
                        {{ $t('message.transactions_admin_plus.no_financial_data_yet') }}
                    </span>
                    <div class="text-body-1 text-empty">
                        {{ $t('message.transactions_admin_plus.empty_state_add_revenue_or_expenses') }}
                    </div>
                </div>
            </v-col>
        </v-row>
    </div>

    <!-- Main dashboard with data -->
    <div v-else>

        <div
            class="d-inline-flex flex-wrap mb-4 rounded-lg mobile-header-financial justify-space-between align-center pa-lg-2 pa-5">
            <v-col cols="12" lg="auto">
                <div class="d-flex flex-row  align-center align-lg-center text-left item_header pb-4">
                    <div class="rounded-lg pa-3 mb-2 mr-2 plusTealLighten5">
                        <v-img :src="require('@/assets/icons/admin+/currency-exchange.svg')" width="36"
                            height="36"></v-img>
                    </div>
                    <div>
                        <div class="font-roboto text-h5">
                            <v-progress-circular size="36" v-if="loading" :width="2"
                                indeterminate></v-progress-circular>
                            <span v-else>{{ formatCurrency(monthlyRevenueAmount) }}</span>
                        </div>
                        <div class="text-body-2 font-roboto">{{ $t('message.transactions_admin_plus.revenue_this_month')
                            }}</div>
                    </div>
                </div>
            </v-col>

            <v-col cols="12" lg="auto">
                <div class="d-flex flex-row  align-center align-lg-center text-left item_header pb-4">
                    <div class="rounded-lg pa-3 mb-2  mr-2 plusAmberLighten5">
                        <v-icon size="36" color="plusDeepOrangeAccent2">mdi-receipt-text-outline</v-icon>
                    </div>
                    <div>
                        <div class="font-roboto text-h5">
                            <v-progress-circular size="20" v-if="loading" :width="2"
                                indeterminate></v-progress-circular>
                            <span v-else>{{ formatCurrency(monthlyExpenseAmount) }}</span>
                        </div>
                        <div class="text-body-2 font-roboto">
                            {{ $t('message.transactions_admin_plus.expenses_this_month') }}</div>
                    </div>
                </div>

            </v-col>

            <v-col cols="12" lg="auto">

                <div class="d-flex flex-row align-center align-lg-center text-left item_header pb-4">
                    <div class="rounded-lg pa-3 mb-2 mr-2 plusLightGreenLighten5">
                        <v-img :src="require('@/assets/icons/admin+/atm_local.svg')" width="36" height="36"></v-img>
                    </div>
                    <div>
                        <div class="font-roboto text-h5">
                            <v-progress-circular size="20" v-if="loading" :width="2"
                                indeterminate></v-progress-circular>
                            <span v-else :class="netProfitAmount >= 0 ? 'success--text' : 'error--text'">{{
                                formatCurrency(netProfitAmount) }}</span>
                        </div>
                        <div class="text-body-2 font-roboto">{{ $t('message.transactions_admin_plus.net_profit') }}
                        </div>
                    </div>
                </div>
            </v-col>
        </div>

        <!-- Revenue Breakdown - This Month -->

        <!-- Dashboard/Quick Look Content -->
        <!-- <v-card outlined elevation="0" class="pa-0 mt-6">
            <div v-allowed="'view_income'">
                <GraphLoader v-if="loadingCurrentMonthIncome">
                </GraphLoader>
                <div v-if="chartDataIncomeMonthly && !loadingCurrentMonthIncome" class="text-center">
                    <v-row align="center">
                        <v-col cols="12" md="3">
                            <v-card elevation="0">
                                <v-card-title class="pl-2 text-subtitle-2 text-center">{{ $t('message.transactions_admin_plus.current_month')
                                    }}
                                    <v-spacer></v-spacer>
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn @click="triggerStatsRefresh(true)" :loading="triggeringRefresh"
                                                elevation="0" icon v-bind="attrs"
                                                v-on="on"><v-icon>mdi-sync</v-icon></v-btn>
                                        </template>
<span>{{ $t('message.transactions_admin_plus.refresh_data') }}</span>
</v-tooltip>
</v-card-title>
<v-card-text class="mb-0 pb-0">
    <div class="text-h3">
        <div v-if="currentMonthTotal > 0" class="ml-2 success--text">
            <b>{{ currencySymbol }}{{
                currentMonthTotal.toFixed(0)
                }}</b>
        </div>
        <div v-else-if="currentMonthTotal < 0" class="ml-2 error--text">
            <b>
                -{{ currencySymbol }}{{ Math.abs(currentMonthTotal).toFixed(0) }}
            </b>
        </div>
    </div>
    <div v-if="payoutsHistory && !isNaN(payoutsHistory.future)">
        <span class="text-subtitle-2 mr-1 text-caption">
            <v-icon class="mr-1">mdi-receipt-clock-outline</v-icon>
            {{ $t('message.transactions_admin_plus.future_payouts') }}: </span>
        <span>{{ currencySymbol }}{{ Number.parseFloat(payoutsHistory.future).toFixed(2)
            }}</span>
    </div>
</v-card-text>
<v-card-actions v-if="!isSmallerDisplay()">
    <div v-if="chartOptionsIncomeMonthly.lastUpdate" class="mt-2 text-center text-caption">{{
        $t('message.transactions_admin_plus.last_update')
        }} {{ $d(new
        Date(chartOptionsIncomeMonthly.lastUpdate), 'long',
        $i18n.locale) }}</div>
</v-card-actions>
</v-card>
</v-col>
<v-col cols="12" md="9">
    <ApexChart type="bar" :options="chartOptionsIncomeMonthly" height="160" width="100%"
        :series="chartDataIncomeMonthly">
    </ApexChart>
    <v-card-actions v-if="isSmallerDisplay()">
        <div v-if="chartOptionsIncomeMonthly.lastUpdate" class="mt-2 text-center text-caption">
            {{
            $t('message.transactions_admin_plus.last_update')
            }} {{ $d(new
            Date(chartOptionsIncomeMonthly.lastUpdate), 'long',
            $i18n.locale) }}</div>
    </v-card-actions>
</v-col>
</v-row>
</div>
<v-card-text v-else-if="!loadingCurrentMonthIncome" class="text-center text-subtitle-1">
    {{ $t('message.transactions_admin_plus.current_month_processing_come_back') }}
</v-card-text>
</div>
</v-card> -->

        <v-card elevation="0" class="pa-0">
            <v-card-title>
                <v-row class="d-flex align-center" block>
                    <v-col cols="8" class="d-flex align-center">
                        <span class="font-roboto text-h5 font-weight-regular">
                            {{ financialPerformanceChartTitle }}
                        </span>
                    </v-col>
                    <v-col cols="4" class="d-flex justify-end align-center">
                        <v-select @change="filterDatedIncome($event)" outlined dense :value="selectedIncomeGraphMonths"
                            @input="$emit('update:selected-income-graph-months', $event)" :items="availableMonths"
                            hide-details :label="$t('message.transactions_admin_plus.months')"></v-select>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-actions class="pt-0 pl-4">
                <v-switch dense hide-details class="mt-0" :label="$t('message.transactions_admin_plus.by_quarter')"
                    :input-value="incomeBarQuarterly" :disabled="!incomeBars"
                    @change="$emit('quarterly-changed', $event)">
                </v-switch>
            </v-card-actions>
            <div v-allowed="'view_income'">
                <GraphLoader type="line" v-if="loadingBottomCharts">
                </GraphLoader>
                <div v-else-if="incomeBars" class="text-center">
                    <ApexChart type="line" :options="incomeBars.options" height="420" width="100%"
                        :series="incomeBars.data">
                    </ApexChart>
                </div>
            </div>
        </v-card>

        <v-card elevation="0" class="pa-0 mt-6">
            <v-row>
                <v-col cols="12" md="6">
                    <v-card-title class="d-flex align-center justify-center justify-md-start">
                        <span class="font-roboto text-h5 font-weight-regular">{{
                            $t('message.transactions_admin_plus.revenue_breakdown_this_month') }}</span>
                    </v-card-title>
                    <div v-if="chartOptionsIncomeMonthly.lastUpdate"
                        class="text-caption text--secondary text-center text-md-left px-4">{{
                            $t('message.transactions_admin_plus.last_update') }} {{ $d(new
                            Date(chartOptionsIncomeMonthly.lastUpdate), 'long', $i18n.locale) }}</div>
                    <v-card-text>
                        <v-row class="justify-center">
                            <v-col cols="9">
                                <div v-if="loadingRevenueDonutChart" class="text-center pa-8">
                                    <v-progress-circular size="64" width="3" indeterminate></v-progress-circular>
                                    <div class="text-center mt-4">
                                        <span class="font-roboto text-subtitle-1">
                                            {{ $t('message.transactions_admin_plus.loading_revenue_breakdown') }}
                                        </span>
                                    </div>
                                </div>
                                <div
                                    v-else-if="revenueDonutChartData && revenueDonutChartData.length > 0 && revenueDonutChartOptions">
                                    <v-row class="align-center">
                                        <v-col cols="12" md="5">
                                            <div class="financial-donut-wrap">
                                                <ApexChart type="donut" :options="styledRevenueDonutOptions"
                                                    :series="revenueDonutChartData" height="320">
                                                </ApexChart>
                                                <div class="financial-donut-total">
                                                    <div class="financial-donut-total-amount">
                                                        {{ formatCurrency(monthlyRevenueAmount) }}
                                                    </div>
                                                    <div class="financial-donut-total-label">{{
                                                        $t('message.transactions_admin_plus.total') }}</div>
                                                </div>
                                            </div>
                                        </v-col>
                                        <v-col cols="12" md="7">
                                            <div v-for="(item, idx) in revenueLegendItems"
                                                :key="`revenue-legend-${idx}`"
                                                class="d-flex align-center justify-space-between mb-4">
                                                <div class="d-flex align-center">
                                                    <span class="financial-legend-dot mr-3"
                                                        :style="{ backgroundColor: item.color }"></span>
                                                    <span class="financial-legend-label">{{ item.label }}</span>
                                                </div>
                                                <span class="financial-legend-value">{{ formatCurrency(item.value)
                                                    }}</span>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </div>
                                <div v-else class="text-center pa-8">
                                    <div class="text-body-1 text--secondary">
                                        {{ $t('message.transactions_admin_plus.no_revenue_data_this_month') }}
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>

                </v-col>


                <!-- Expense Breakdown - This Month -->

                <v-col cols="12" lg="6" class="mt-4 mt-md-0">

                    <v-card-title class="d-flex align-center justify-center justify-md-start">
                        <span class="font-roboto text-h5 font-weight-regular">{{
                            $t('message.transactions_admin_plus.expense_breakdown_this_month') }}</span>
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12">
                                <div v-if="loadingExpenseDonutChart" class="text-center pa-8">
                                    <v-progress-circular size="64" width="3" indeterminate></v-progress-circular>
                                    <div class="text-center mt-4">
                                        <span class="font-roboto text-subtitle-1">
                                            {{ $t('message.transactions_admin_plus.loading_expense_breakdown') }}
                                        </span>
                                    </div>
                                </div>
                                <div
                                    v-else-if="expenseDonutChartData && expenseDonutChartData.length > 0 && expenseDonutChartOptions">
                                    <v-row class="align-center justify-center">
                                        <v-col cols="10" md="5">
                                            <div class="financial-donut-wrap">
                                                <ApexChart type="donut" :options="styledExpenseDonutOptions"
                                                    :series="expenseDonutChartData" height="320">
                                                </ApexChart>
                                                <div class="financial-donut-total">
                                                    <div class="financial-donut-total-amount">
                                                        {{ formatCurrency(monthlyExpenseAmount) }}
                                                    </div>
                                                    <div class="financial-donut-total-label">{{
                                                        $t('message.transactions_admin_plus.total') }}</div>
                                                </div>
                                            </div>
                                        </v-col>
                                        <v-col cols="12" md="7">
                                            <div v-for="(item, idx) in expenseLegendItems"
                                                :key="`expense-legend-${idx}`"
                                                class="d-flex align-center justify-space-between mb-5">
                                                <div class="d-flex align-center">
                                                    <span class="financial-legend-dot mr-3"
                                                        :style="{ backgroundColor: item.color }"></span>
                                                    <span class="financial-legend-label">{{ item.label }}</span>
                                                </div>
                                                <span class="financial-legend-value">{{ formatCurrency(item.value)
                                                }}</span>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </div>
                                <div v-else class="text-center pa-8">
                                    <div class="text-body-1 text--secondary">
                                        {{ $t('message.transactions_admin_plus.no_expense_data_this_month') }}
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-col>
            </v-row>
        </v-card>
        <!-- Top Products Graph -->
        <v-card elevation="0" class="pa-0 mt-6">
            <v-card-title>
                <v-row class="d-flex align-center" block>
                    <v-col cols="8" class="d-flex align-center">
                        <span class="font-roboto text-h5 font-weight-regular">
                            {{ $t('message.transactions_admin_plus.top_products') }}
                        </span>
                    </v-col>
                    <v-col cols="4" class="d-flex justify-end align-center">
                        <v-select outlined dense :value="selectedProductsGraphMonths"
                            @input="$emit('update:selected-products-graph-months', $event)" :items="availableMonths"
                            hide-details :label="$t('message.transactions_admin_plus.months')"></v-select>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text>
                <v-row class="justify-center">
                    <v-col cols="12" md="9">
                        <div v-if="!productsGraph || !productsGraph.ready" class="text-center pa-8">
                            <v-progress-circular size="64" width="3" indeterminate></v-progress-circular>
                        </div>
                        <div
                            v-else-if="productsGraph.thisYear && productsGraph.thisYear.data && productsGraph.thisYear.data.length > 0">
                            <v-row class="align-center">
                                <v-col cols="12" md="5">
                                    <div class="financial-donut-wrap">
                                        <ApexChart type="donut" :options="styledProductsDonutOptions"
                                            :series="productsGraph.thisYear.data" height="320">
                                        </ApexChart>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="7">
                                    <div v-for="(item, idx) in productsLegendItems" :key="`product-legend-${idx}`"
                                        class="d-flex align-center justify-space-between mb-4">
                                        <div class="d-flex align-center">
                                            <span class="financial-legend-dot mr-3"
                                                :style="{ backgroundColor: item.color }"></span>
                                            <span class="financial-legend-label">{{ item.label }}</span>
                                        </div>
                                        <span class="financial-legend-value">{{ item.count }}</span>
                                    </div>
                                </v-col>
                            </v-row>
                        </div>
                        <div v-else class="text-center pa-8">
                            <div class="text-body-1 text--secondary">
                                {{ $t('message.transactions_admin_plus.no_revenue_data_this_month') }}
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- Future Payouts -->
        <v-card v-if="stripeReady" elevation="0" class="pa-0 mt-6">
            <v-card-title>
                <span class="font-roboto text-h5 font-weight-regular">
                    {{ $t('message.transactions_admin_plus.future_payouts') }}
                </span>
            </v-card-title>
            <div v-if="payoutsHistory && payoutsHistory.loading" class="text-center pa-8">
                <v-progress-circular size="64" width="3" indeterminate></v-progress-circular>
            </div>
            <div v-else-if="payoutsHistory && payoutsHistory.data && payoutsHistory.data.length">
                <v-card-text class="pb-0">
                    <v-row>
                        <v-col v-if="payoutsHistory.connectReserved > 0" cols="12" md="4">
                            <div class="d-flex align-center">
                                <v-icon class="mr-2" small>mdi-receipt-clock-outline</v-icon>
                                <span class="text-body-2 mr-1">{{
                                    $t('message.transactions_admin_plus.pending_confirmation')
                                    }}:</span>
                                <span class="text-body-2">{{ formatCurrency(payoutsHistory.connectReserved) }}</span>
                            </div>
                        </v-col>
                        <v-col v-if="!isNaN(parseFloat(String(payoutsHistory.future)))" cols="12" md="4">
                            <div class="d-flex align-center">
                                <v-icon class="mr-2" small>mdi-receipt-clock-outline</v-icon>
                                <span class="text-body-2 mr-1">{{ $t('message.transactions_admin_plus.future_payouts')
                                    }}:</span>
                                <span class="text-body-2">{{ formatCurrency(payoutsHistory.future) }}</span>
                            </div>
                        </v-col>
                        <v-col v-if="!isNaN(parseFloat(String(payoutsHistory.inTransit)))" cols="12" md="4">
                            <div class="d-flex align-center">
                                <v-icon class="mr-2" small>mdi-truck-fast-outline</v-icon>
                                <span class="text-body-2 mr-1">{{ $t('message.transactions_admin_plus.in_transit')
                                    }}:</span>
                                <span class="text-body-2">{{ formatCurrency(payoutsHistory.inTransit) }}</span>
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-data-table v-if="!isSmallerDisplay()" :headers="payoutsHistory.headers" :items="payoutsHistory.data"
                    :items-per-page="5" class="elevation-0">
                    <template v-slot:item.started="{ item }">
                        {{ $d(new Date(item.started), "short", $i18n.locale) }}
                    </template>
                    <template v-slot:item.arrival="{ item }">
                        {{ $d(new Date(item.arrival), "short", $i18n.locale) }}
                    </template>
                    <template v-slot:item.status="{ item }">
                        <v-chip small :color="getPayoutStatusColor(item.status)">
                            {{ String($t('message.transactions_admin_plus.' + item.status)).toLowerCase() }}
                        </v-chip>
                    </template>
                    <template v-slot:item.amount="{ item }">
                        {{ formatCurrency(item.amount) }}
                    </template>
                </v-data-table>
                <v-simple-table v-else fixed-header height="300px">
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th class="text-left">{{ $t("message.transactions_admin_plus.arriving") }}</th>
                                <th class="text-left">{{ $t("message.transactions_admin_plus.amount") }}</th>
                                <th class="text-left">{{ $t("message.transactions_admin_plus.status") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in payoutsHistory.data" :key="item.id">
                                <td>{{ $d(new Date(item.arrival), "short", $i18n.locale) }}</td>
                                <td>{{ formatCurrency(item.amount) }}</td>
                                <td>
                                    <v-chip small :color="getPayoutStatusColor(item.status)">
                                        {{ String($t('message.transactions_admin_plus.' + item.status)).toLowerCase() }}
                                    </v-chip>
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </div>
            <v-card-text v-else class="text-center pa-8">
                <div class="text-body-1 text--secondary">
                    {{ $t('message.transactions_admin_plus.no_revenue_data_this_month') }}
                </div>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import GraphLoader from "@/components/displays/GraphLoader.vue";
import ApexChart from 'vue-apexcharts';

export default {
    name: "FinancialOverview",
    components: {
        GraphLoader,
        ApexChart
    },
    props: {
        academy: {
            type: Object,
            default: null
        },
        currencySymbol: {
            type: String,
            default: '$'
        },
        financialData: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        loadingCurrentMonthIncome: {
            type: Boolean,
            default: false
        },
        chartDataIncomeMonthly: {
            default: false
        },
        chartOptionsIncomeMonthly: {
            type: Object,
            default: () => ({})
        },
        currentMonthTotal: {
            type: Number,
            default: 0
        },
        payoutsHistory: {
            type: Object,
            default: () => ({ loading: true, headers: [], data: [] })
        },
        triggeringRefresh: {
            type: Boolean,
            default: false
        },
        loadingBottomCharts: {
            type: Boolean,
            default: false
        },
        selectedIncomeGraphMonths: {
            type: Number,
            default: 12
        },
        availableMonths: {
            type: Array,
            default: () => []
        },
        incomeBarQuarterly: {
            type: Boolean,
            default: false
        },
        incomeBars: {
            default: null
        },
        productsGraph: {
            type: Object,
            default: () => ({ ready: false, thisYear: {} })
        },
        pieChartSelection: {
            default: null
        },
        loadingIncomeTrendline: {
            type: Boolean,
            default: false
        },
        incomeTrendline: {
            default: null
        },
        stripeReady: {
            type: Boolean,
            default: false
        },
        paymentsReady: {
            type: Boolean,
            default: false
        },
        revenueDonutChartData: {
            type: Array,
            default: () => []
        },
        revenueDonutChartOptions: {
            type: Object,
            default: () => ({})
        },
        loadingRevenueDonutChart: {
            type: Boolean,
            default: false
        },
        expenseDonutChartData: {
            type: Array,
            default: () => []
        },
        expenseDonutChartOptions: {
            type: Object,
            default: () => ({})
        },
        loadingExpenseDonutChart: {
            type: Boolean,
            default: false
        },
        selectedProductsGraphMonths: {
            type: Number,
            default: 12
        },
        loadingProductsGraph: {
            type: Boolean,
            default: false
        }
    },
    mounted() {
        /*
        console.log('=== FinancialOverview Component Mounted ===');
        console.log('Academy data:', this.academy);
        console.log('Financial data:', this.financialData);
        console.log('Currency symbol:', this.currencySymbol);
        console.log('Loading state:', this.loading);
        console.log('Current Month Total:', this.currentMonthTotal);
        console.log('All Props:', this.$props);
        // Printar dados específicos se academy existir
        if (this.academy) {
            console.log('Academy ID:', this.academy.id);
            console.log('Academy Name:', this.academy.name);
            console.table(this.financialData); // Mostra dados em tabela no console
        }
        console.log('=== End Component Mount Info ===');
        */
    },
    emits: [
        'trigger-stats-refresh',
        'filter-dated-income',
        'build-income-bars',
        'close-pie-chart-selection',
        'update:selected-income-graph-months',
        'update:selected-products-graph-months',
        'update:income-bar-quarterly',
        'quarterly-changed'
    ],
    computed: {
        isLoading() {
            return this.isLoadingState();
        },
        shouldShowEmpty() {
            return this.isEmpty();
        },
        monthlyRevenueAmount() {
            return (this.revenueDonutChartData || []).reduce((/** @type {number} */ sum, /** @type {number | string} */ value) => sum + (parseFloat(String(value)) || 0), 0);
        },
        revenueChartLabels() {
            const options = this.revenueDonutChartOptions || {};
            return Array.isArray(options.labels) ? options.labels : [];
        },
        revenueChartColors() {
            const options = this.revenueDonutChartOptions || {};
            return Array.isArray(options.colors) ? options.colors : [];
        },
        styledRevenueDonutOptions() {
            const options = this.revenueDonutChartOptions || {};
            return {
                ...options,
                legend: { show: false },
                dataLabels: { enabled: false },
                stroke: {
                    show: true,
                    width: 4,
                    colors: ['#FFFFFF']
                },
                plotOptions: {
                    ...(options.plotOptions || {}),
                    pie: {
                        ...((options.plotOptions && options.plotOptions.pie) || {}),
                        donut: {
                            ...((options.plotOptions && options.plotOptions.pie && options.plotOptions.pie.donut) || {}),
                            size: '86%',
                            labels: {
                                show: false
                            }
                        }
                    }
                }
            };
        },
        revenueLegendItems() {
            const labels = this.revenueChartLabels;
            const colors = this.revenueChartColors;
            const series = Array.isArray(this.revenueDonutChartData) ? this.revenueDonutChartData : [];
            return series.map((/** @type {number | string} */ value, /** @type {number} */ idx) => ({
                label: labels[idx] || this.$t('message.transactions_admin_plus.financial_category_fallback', [idx + 1]),
                color: colors[idx] || '#3F51B5',
                value: parseFloat(String(value)) || 0
            }));
        },
        expenseChartLabels() {
            const options = this.expenseDonutChartOptions || {};
            return Array.isArray(options.labels) ? options.labels : [];
        },
        expenseChartColors() {
            const options = this.expenseDonutChartOptions || {};
            return Array.isArray(options.colors) ? options.colors : [];
        },
        styledExpenseDonutOptions() {
            const options = this.expenseDonutChartOptions || {};
            return {
                ...options,
                legend: { show: false },
                dataLabels: { enabled: false },
                stroke: {
                    show: true,
                    width: 4,
                    colors: ['#FFFFFF']
                },
                plotOptions: {
                    ...(options.plotOptions || {}),
                    pie: {
                        ...((options.plotOptions && options.plotOptions.pie) || {}),
                        donut: {
                            ...((options.plotOptions && options.plotOptions.pie && options.plotOptions.pie.donut) || {}),
                            size: '86%',
                            labels: {
                                show: false
                            }
                        }
                    }
                }
            };
        },
        expenseLegendItems() {
            const labels = this.expenseChartLabels;
            const colors = this.expenseChartColors;
            const series = Array.isArray(this.expenseDonutChartData) ? this.expenseDonutChartData : [];
            return series.map((/** @type {number | string} */ value, /** @type {number} */ idx) => ({
                label: labels[idx] || this.$t('message.transactions_admin_plus.financial_category_fallback', [idx + 1]),
                color: colors[idx] || '#3F51B5',
                value: parseFloat(String(value)) || 0
            }));
        },
        monthlyExpenseAmount() {
            return (this.expenseDonutChartData || []).reduce((/** @type {number} */ sum, /** @type {number | string} */ value) => sum + (parseFloat(String(value)) || 0), 0);
        },
        netProfitAmount() {
            const hasBreakdownData = (this.revenueDonutChartData && this.revenueDonutChartData.length > 0) ||
                (this.expenseDonutChartData && this.expenseDonutChartData.length > 0);
            if (hasBreakdownData) {
                return this.monthlyRevenueAmount - this.monthlyExpenseAmount;
            }
            return parseFloat(this.currentMonthTotal) || 0;
        },
        hasValidData() {
            return this.hasFinancialData();
        },
        financialPerformanceChartTitle() {
            if (this.isSmallerDisplay()) {
                return this.$t('message.transactions_admin_plus.financial_trends');
            }
            const n = this.selectedIncomeGraphMonths;
            if (n > 1) {
                return this.$t('message.transactions_admin_plus.financial_performance_last_n_months', [n]);
            }
            return this.$t('message.transactions_admin_plus.financial_performance_last_single_month');
        },
        productsChartLabels() {
            const opts = (this.productsGraph && this.productsGraph.thisYear && this.productsGraph.thisYear.options) || {};
            return Array.isArray(opts.labels) ? opts.labels : [];
        },
        productsChartColors() {
            const opts = (this.productsGraph && this.productsGraph.thisYear && this.productsGraph.thisYear.options) || {};
            return Array.isArray(opts.colors) && opts.colors.length > 0 ? opts.colors : [];
        },
        styledProductsDonutOptions() {
            const base = (this.productsGraph && this.productsGraph.thisYear && this.productsGraph.thisYear.options) || {};
            return {
                ...base,
                colors: this.productsChartColors,
                theme: { mode: this.isDarkMode() ? 'dark' : 'light' },
                legend: { show: false },
                dataLabels: { enabled: false },
                stroke: {
                    show: true,
                    width: 4,
                    colors: ['#FFFFFF']
                },
                plotOptions: {
                    ...(base.plotOptions || {}),
                    pie: {
                        ...((base.plotOptions && base.plotOptions.pie) || {}),
                        donut: {
                            ...((base.plotOptions && base.plotOptions.pie && base.plotOptions.pie.donut) || {}),
                            size: '86%',
                            labels: { show: false }
                        }
                    }
                }
            };
        },
        productsLegendItems() {
            const labels = this.productsChartLabels;
            const colors = this.productsChartColors;
            const series = (this.productsGraph && this.productsGraph.thisYear && Array.isArray(this.productsGraph.thisYear.data))
                ? this.productsGraph.thisYear.data : [];
            return series.map((/** @type {number} */ value, /** @type {number} */ idx) => ({
                label: labels[idx] || String(idx + 1),
                color: colors[idx] || '#3F51B5',
                count: value
            }));
        }
    },
    methods: {
        triggerStatsRefresh(/** @type {boolean} */ force) {
            this.$emit('trigger-stats-refresh', force);
        },
        filterDatedIncome(months = null) {
            this.$emit('filter-dated-income', months);
        },
        buildIncomeBars() {
            this.$emit('build-income-bars');
        },
        formatCurrency(/** @type {number | string} */ value) {
            const numeric = parseFloat(String(value)) || 0;
            const sign = numeric < 0 ? '-' : '';
            const absolute = Math.abs(numeric);
            const formatted = absolute.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
            return `${sign}${this.currencySymbol}${formatted}`;
        },
        getPayoutStatusColor(/** @type {string} */ status) {
            if (status === 'paid' || status === 'success' || status === 'succeeded') return this.themeColor('success');
            if (status === 'pending') return this.themeColor('pending');
            if (status === 'in_transit') return this.themeColor('gold');
            if (status === 'canceled' || status === 'failed') return this.themeColor('error');
            return null;
        },
        isNotHaveTransactions() {
            return !this.academy || !this.academy.transactions || this.academy.transactions.length === 0;
        },
        hasFinancialData() {
            const hasBasicData = this.academy &&
                this.academy.transactions &&
                this.academy.transactions.length > 0;

            const hasFinancialData = this.financialData &&
                this.financialData.length > 0;

            const hasMonthlyIncomeData = this.chartDataIncomeMonthly &&
                !this.loadingCurrentMonthIncome;

            const hasCurrentMonthTotal = this.currentMonthTotal !== undefined &&
                this.currentMonthTotal !== null;

            const hasPayoutsData = this.payoutsHistory &&
                this.payoutsHistory.data &&
                this.payoutsHistory.data.length > 0 &&
                !this.payoutsHistory.loading;

            const hasChartData = this.incomeBars ||
                (this.productsGraph && this.productsGraph.ready) ||
                this.incomeTrendline;

            const hasPaymentSetup = this.stripeReady && this.paymentsReady;

            return hasBasicData ||
                hasFinancialData ||
                (hasMonthlyIncomeData && hasCurrentMonthTotal) ||
                hasPayoutsData ||
                hasChartData ||
                hasPaymentSetup;
        },
        isEmpty() {
            return !this.hasFinancialData() && !this.loading;
        },
        isLoadingState() {
            return this.loadingCurrentMonthIncome ||
                this.loadingBottomCharts ||
                this.loadingIncomeTrendline ||
                this.triggeringRefresh ||
                (this.payoutsHistory && this.payoutsHistory.loading);
        }
    },
    watch: {
        academy: {
            handler(newVal, oldVal) {
                console.log('Academy changed:', { old: oldVal, new: newVal });
            },
            deep: true,
            immediate: true
        },
        financialData: {
            handler(newVal) {
                console.log('Financial data updated:', newVal);
            },
            deep: true
        },
        loading(newVal) {
            console.log('Loading state changed:', newVal);
        }
    }
};
</script>

<style>
.mobile-header-financial {
    border: 1px solid #E8EAF6 !important;
    padding: 1.25rem 1rem 1.25rem 1rem !important;

}

@media (min-width: 1264px) {
    .mobile-header-financial {
        border: none !important;
        border-top: none !important;
        padding: 0 !important;
    }

    .mobile-header-financial .item_header {
        border: none !important;
    }
}

.mobile-header-financial .item_header {
    border-bottom: 1px solid #E8EAF6;
}

.financial-donut-wrap {
    position: relative;
}

.financial-donut-total {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    pointer-events: none;
    text-align: center;
}

.financial-donut-total-amount {
    font-family: "Roboto", sans-serif;
    font-size: 1.5rem;
    line-height: 1.05;
    font-weight: 400;
    color: #000000;
}

.financial-donut-total-label {
    margin-top: 6px;
    font-family: "Roboto", sans-serif;
    font-size: 0.9rem;
    line-height: 1.05;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.60);
}

.financial-legend-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    display: inline-block;
}

.financial-legend-label {
    font-family: "Roboto", sans-serif;
    font-size: 0.8rem;
    line-height: 1.2;
    font-weight: 400;
    color: #141414;
}

.financial-legend-value {
    font-family: "Roboto", sans-serif;
    font-size: 0.8rem;
    line-height: 1.2;
    font-weight: 400;
    color: #616161;
}

@media (max-width: 1264px) {
    .financial-donut-total-amount {
        font-size: 2.1rem;
    }

    .financial-donut-total-label {
        font-size: 1.4rem;
    }

    .financial-legend-label {
        font-size: 1.1rem;
    }

    .financial-legend-value {
        font-size: 1.1rem;
    }
}
</style>